<template>
  <main
      class="chat-main"
      :class="{ 'dragging-file': isGlobalDragOver }"
      @dragenter.prevent="handleGlobalDragEnter"
      @dragleave.prevent="handleGlobalDragLeave"
      @dragover.prevent="handleGlobalDragOver"
      @drop.prevent="handleGlobalDrop"
  >
    <!-- Global drag overlay -->
    <div v-if="isGlobalDragOver" class="global-drag-overlay">
      <div class="drag-overlay-content">
        <el-icon class="close-drag-btn" @click="closeDragOverlay"><Close /></el-icon>
        <el-icon :size="60" color="#4e6ef2"><UploadFilled /></el-icon>
        <p class="drag-overlay-text">释放以上传文件</p>
      </div>
    </div>
    <div class="chat-header">
      <div class="chat-header-left">
        <el-image
            src="images/layout-sidebar-right.png"
            class="toggle-sidebar-btn"
            @click="emit('toggleSidebar')"
        />
      </div>
      <div class="chat-header-center">
        <span class="chat-title">{{ conversationTitle }}</span>
<!--        <span class="chat-model-tag">{{ currentModelName }}</span>-->
      </div>
      <div class="chat-header-right">
      </div>
    </div>
    <!-- WELCOME SCREEN -->
    <template v-if="!activeConversation">
      <div class="welcome-view">
        <div class="welcome-content">
          <div class="welcome-icon">
            <el-image src="images/Group 2147234562.png" alt="logo" />
          </div>
          <div class="input-area">
            <ChatInput
                v-model="inputText"
                :disabled="isStreaming"
                :model-name="currentModelName"
                @send="sendMessage()"
                @keydown="handleKeydown"
                @file-upload="handleFileUpload">
            <!-- 通过插槽将待发送文件传递到 ChatInput 内部 -->
            <template #fileList>
              <div v-if="pendingFiles.length > 0" class="file-list">
                <div v-for="file in pendingFiles" :key="file.id" class="file-item">
                  <el-icon :size="16"><Document /></el-icon>
                  <span class="file-name">{{ file.file.name }}</span>
                  <el-icon class="remove-file" @click.stop="removePendingFile(file.id)"><Close /></el-icon>
                </div>
              </div>
            </template>
            </ChatInput>
          </div>
        </div>
      </div>
    </template>

    <!-- CHAT SCREEN -->
    <template v-else>
      <div class="chat-view">
        <!-- Messages -->
        <div class="messages-container" ref="messagesRef">
          <div class="messages-inner">
            <MessageItem
                v-for="(msg, idx) in messages"
                :key="msg.id"
                :message="msg"
                :is-last="idx === messages.length - 1"
                @regenerate="handleRegenerate"
            />

            <!-- Thinking dots (AI hasn't started outputting yet) -->
            <div
                v-if="isStreaming && messages.length > 0 && !messages[messages.length - 1].content.trim()"
                class="thinking-indicator"
            >
              <div class="thinking-dots">
                <span /><span /><span />
              </div>
              <span class="thinking-text">小安 正在思考...</span>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="input-area">
          <ChatInput
              v-model="inputText"
              :disabled="isStreaming"
              :model-name="currentModelName"
              @send="sendMessage()"
              @keydown="handleKeydown"
              @file-upload="handleFileUpload"
          >
            <!-- 通过插槽将待发送文件传递到 ChatInput 内部 -->
            <template #fileList>
              <div v-if="pendingFiles.length > 0" class="file-list">
                <div v-for="file in pendingFiles" :key="file.id" class="file-item">
                  <el-icon :size="16"><Document /></el-icon>
                  <span class="file-name">{{ file.file.name }}</span>
                  <el-icon class="remove-file" @click.stop="removePendingFile(file.id)"><Close /></el-icon>
                </div>
              </div>
            </template>
          </ChatInput>
        </div>
      </div>
    </template>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import {useChatStore} from '@/stores/chat'
import useUserStore from "@/stores/user";
import MessageItem from './MessageItem.vue'
import ChatInput from './ChatInput.vue'
import { SUGGESTIONS, MODELS, type UploadedFile } from '@/types'
import { UploadFilled, Close, Document } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const emit = defineEmits(['toggleSidebar'])

const chatStore = useChatStore()
const userStore = useUserStore()
const { isLoggedIn } = storeToRefs(userStore)
const { activeConversation, isStreaming, selectedModel } = storeToRefs(chatStore)

const inputText = ref('')
const messagesRef = ref<HTMLElement | null>(null)
const isGlobalDragOver = ref(false)
const pendingFiles = ref<UploadedFile[]>([]) // 暂存待发送的文件

const currentModelName = computed(
    () => MODELS.find(m => m.id === selectedModel.value)?.name ?? 'QWen3'
)
const messages = computed(() => activeConversation.value?.messages ?? [])

const conversationTitle = computed(() => {
  if (!activeConversation.value) return '新对话'
  return activeConversation.value.title || '新对话'
})

// 监听 activeConversation 变化，确保视图更新
watch(activeConversation, (newConv) => {
  console.log('👀 activeConversation 变化:', newConv ? '有对话' : '无对话')
  console.log('👀 对话 ID:', newConv?.id)
  console.log('👀 对话标题:', newConv?.title)
}, { immediate: true, deep: true })

// Auto-scroll when message content changes (streaming)
watch(
    () => {
      const msgs = messages.value
      return msgs.length > 0 ? msgs[msgs.length - 1].content : ''
    },
    async () => {
      await nextTick()
      scrollToBottom()
    }
)

// Scroll when switching conversations
watch(activeConversation, async () => {
  await nextTick()
  scrollToBottom()
})

function scrollToBottom() {
  if (messagesRef.value) {
    messagesRef.value.scrollTop = messagesRef.value.scrollHeight
  }
}

async function sendMessage(text?: string) {
  const content = text !== undefined ? text : inputText.value
  if (!content.trim() || isStreaming.value) return
  inputText.value = ''
  // 传递文件参数
  const filesToSend = pendingFiles.value.length > 0 ? [...pendingFiles.value] : undefined
  await chatStore.sendMessage(content, filesToSend)

  // 发送完成后清空文件列表
  pendingFiles.value = []
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendMessage()
  }
}

async function handleSuggestion(text: string) {
  chatStore.newConversation()
  await nextTick()
  await sendMessage(text)
}

async function handleRegenerate() {
  const conv = activeConversation.value
  if (!conv || isStreaming.value) return
  const msgs = conv.messages
  const lastUserIdx = [...msgs].reverse().findIndex(m => m.role === 'user')
  if (lastUserIdx === -1) return
  const userMsg = msgs[msgs.length - 1 - lastUserIdx]
  // remove last AI message
  const lastAiIdx = [...msgs].reverse().findIndex(m => m.role === 'assistant')
  if (lastAiIdx !== -1) {
    msgs.splice(msgs.length - 1 - lastAiIdx, 1)
  }
  // remove last user message and re-send
  msgs.splice(msgs.indexOf(userMsg), 1)
  await chatStore.sendMessage(userMsg.content)
}

function handleFileUpload(files: UploadedFile[]) {
  console.log('📎 接收到上传文件:', files)
  for (const uploadedFile of files) {
    const file = uploadedFile.file
    console.log(`  - ${file.name} (${file.size} bytes, ${file.type})`)
  }
  // 将文件添加到待发送列表
  pendingFiles.value = [...pendingFiles.value, ...files]

  ElMessage.success(`准备发送 ${files.length} 个文件`)
}
function removePendingFile(fileId: string) {
  pendingFiles.value = pendingFiles.value.filter(f => f.id !== fileId)
  if (pendingFiles.value.length === 0) {
    ElMessage.info('已取消所有文件')
  } else {
    ElMessage.success(`已移除 1 个文件，剩余 ${pendingFiles.value.length} 个`)
  }
}
// 全局拖拽处理
function handleGlobalDragEnter(e: DragEvent) {
  e.preventDefault()
  isGlobalDragOver.value = true
  console.log('🌍 检测到全局拖拽事件')
}

function handleGlobalDragLeave(e: DragEvent) {
  e.preventDefault()
  // 只有当拖拽真正离开页面时才隐藏
  if (e.clientX === 0 && e.clientY === 0) {
    isGlobalDragOver.value = false
  }
}

function handleGlobalDragOver(e: DragEvent) {
  e.preventDefault()
  e.dataTransfer!.dropEffect = 'copy'
}

function handleGlobalDrop(e: DragEvent) {
  e.preventDefault()
  isGlobalDragOver.value = false

  const files = e.dataTransfer?.files
  if (files && files.length > 0) {
    console.log('全局拖拽上传文件:', files)
    // 触发 ChatInput 的文件上传
    const uploadedFiles: UploadedFile[] = []
    for (let i = 0; i < files.length; i++) {
      uploadedFiles.push({
        id: `global_file_${Date.now()}_${i}`,
        file: files[i],
        uploading: false,
        progress: 0
      })
    }
    handleFileUpload(uploadedFiles)
  }
}
// 关闭拖拽遮罩层
function closeDragOverlay() {
  isGlobalDragOver.value = false
  console.log('用户手动关闭拖拽上传')
}
</script>

<style scoped>
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  //background: linear-gradient( 0deg, #ebf1ff 0%, #FDFEFF 100%);
  background: #ffffff;
  position: relative;
}

/* Global drag overlay */
.global-drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(78, 110, 242, 0.1);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease-out;
}

.drag-overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(78, 110, 242, 0.2);
  border: 3px dashed #4e6ef2;
}
.close-drag-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 50%;
}

.close-drag-btn:hover {
  color: #333;
  background: rgba(0, 0, 0, 0.05);
}
.drag-overlay-text {
  font-size: 18px;
  font-weight: 600;
  color: #4e6ef2;
  margin: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Navigation Bar */
.nav-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #f0f0f0;
  z-index: 1000;
}

.nav-content {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ---- Welcome ---- */
.welcome-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.welcome-content {
  flex: 0.8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  overflow-y: auto;
}

.welcome-icon {
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;
}
.welcome-icon svg { width: 68px; height: 68px; }

.welcome-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1e;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}
.welcome-subtitle {
  font-size: 16px;
  color: #999;
  margin-bottom: 36px;
}

.suggestions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 720px;
}
.suggestion-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 16px;
  background: #f8f9fb;
  border: 1.5px solid #eee;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
}
.suggestion-card:hover {
  background: #f0f3ff;
  border-color: #c7d2fe;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(78, 110, 242, 0.1);
}
.suggestion-icon { font-size: 20px; }
.suggestion-text {
  font-size: 13px;
  color: #444;
  line-height: 1.4;
}

/* ---- Chat ---- */
.chat-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 50px; /* Account for nav bar */
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
}
.chat-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.toggle-sidebar-btn {
  width: 24px;
  height: 24px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}
.toggle-sidebar-btn:hover {
  opacity: 1;
}
.chat-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
}
.chat-model-tag {
  font-size: 11px;
  color: #7c6ef2;
  background: #f0eeff;
  padding: 2px 8px;
  border-radius: 100px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}
.header-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #999;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.15s;
}
.header-btn:hover { background: #f5f5f5; color: #333; }

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
}
.messages-container::-webkit-scrollbar { width: 5px; }
.messages-container::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 3px; }

.messages-inner {
  max-width: 800px;
  margin: 0 auto;
  padding: 16px 0 24px;
}

/* Thinking indicator */
.thinking-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0 12px 44px;
}
.thinking-dots {
  display: flex;
  gap: 5px;
}
.thinking-dots span {
  width: 7px;
  height: 7px;
  background: #4e6ef2;
  border-radius: 50%;
  animation: bounce 1.2s ease-in-out infinite;
}
.thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
.thinking-dots span:nth-child(3) { animation-delay: 0.4s; }
.thinking-text { font-size: 13px; color: #aaa; }

/* Input area (shared) */
.input-area {
  padding: 12px 24px 20px;
  flex-shrink: 0;
  max-width: 848px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  align-self: center;
}
.file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: 8px;
}
.file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0f3ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  font-size: 12px;
}
.file-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #4e6ef2;
}
.remove-file {
  cursor: pointer;
  color: #999;
  transition: color 0.15s;
}
.remove-file:hover {
  color: #f56c6c;
}
/* Animations */
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
@keyframes bounce {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-6px); }
}
</style>
